import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Cpu, Activity, DollarSign, Zap, CheckCircle2, XCircle, AlertTriangle,
  Send, Loader2, RefreshCw, BarChart3, Clock, ArrowUpRight, ArrowDownRight,
  Sparkles, Globe, Brain, Server, TestTube, ChevronDown,
} from "lucide-react";

type GatewayTab = "models" | "costs" | "test";

// ─── Provider logos/colors ────────────────────────────────────────────────────

const PROVIDER_STYLES: Record<string, { color: string; bg: string; border: string }> = {
  openai: { color: "text-success", bg: "bg-success/10", border: "border-success/20" },
  anthropic: { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  google: { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  deepseek: { color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20" },
  "nano-banana": { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
};

function getProviderStyle(provider: string) {
  const key = provider.toLowerCase().replace(/\s+/g, "-");
  return PROVIDER_STYLES[key] || { color: "text-muted-foreground", bg: "bg-secondary", border: "border-border" };
}

// ─── Models Tab ───────────────────────────────────────────────────────────────

function ModelsTab() {
  const { data: modelsData, isLoading, refetch } = trpc.llm.listModels.useQuery();

  const models = modelsData || [];

  // Group by provider
  const grouped = models.reduce((acc: Record<string, typeof models>, m) => {
    const provider = m.provider || "Unknown";
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(m);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-5">
            <div className="h-5 bg-muted rounded w-1/4 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{models.length} modelos configurados</p>
        <button onClick={() => refetch()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {Object.entries(grouped).map(([provider, providerModels]) => {
        const style = getProviderStyle(provider);
        const healthyCount = providerModels.filter((m) => m.status === "healthy").length;
        return (
          <div key={provider} className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center`}>
                  <Server className={`w-4 h-4 ${style.color}`} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{provider}</h3>
                  <p className="text-[10px] text-muted-foreground">{providerModels.length} modelos · {healthyCount} saudáveis</p>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                healthyCount === providerModels.length
                  ? "bg-success/10 text-success border-success/20"
                  : healthyCount > 0
                  ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                  : "bg-destructive/10 text-destructive border-destructive/20"
              }`}>
                {healthyCount === providerModels.length ? "Todos OK" : healthyCount > 0 ? "Parcial" : "Offline"}
              </span>
            </div>
            <div className="divide-y divide-border">
              {providerModels.map((model) => (
                <div key={model.id} className="px-5 py-3 flex items-center gap-4 hover:bg-secondary/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{model.name || model.id}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{model.id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {model.inputCost !== undefined && (
                      <span className="text-[10px] text-muted-foreground">
                        ${model.inputCost}/1M in · ${model.outputCost}/1M out
                      </span>
                    )}
                    {model.status === "healthy" ? (
                      <CheckCircle2 className="w-4 h-4 text-success" />
                    ) : model.status === "degraded" ? (
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-destructive" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {models.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-xl">
          <Cpu className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-foreground mb-1">Nenhum modelo configurado</h3>
          <p className="text-xs text-muted-foreground">Configure o LLM Gateway para ver os modelos disponíveis.</p>
        </div>
      )}
    </div>
  );
}

// ─── Costs Tab ────────────────────────────────────────────────────────────────

function CostsTab() {
  const { data: costsData, isLoading } = trpc.llm.getCosts.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
              <div className="h-3 bg-muted rounded w-1/2 mb-3" />
              <div className="h-6 bg-muted rounded w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const costs = costsData || { totalCost: 0, totalTokens: 0, totalRequests: 0, byModel: [], byDay: [] };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Custo Total</span>
          </div>
          <p className="text-xl font-bold text-foreground">${(costs.totalCost || 0).toFixed(4)}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Últimos 30 dias</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground">Tokens Usados</span>
          </div>
          <p className="text-xl font-bold text-foreground">{((costs.totalTokens || 0) / 1000).toFixed(1)}K</p>
          <p className="text-[10px] text-muted-foreground mt-1">Input + Output</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-success" />
            <span className="text-xs text-muted-foreground">Requisições</span>
          </div>
          <p className="text-xl font-bold text-foreground">{costs.totalRequests || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Total de chamadas</p>
        </div>
      </div>

      {/* Cost by Model */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" /> Custo por Modelo
        </h3>
        {costs.byModel && costs.byModel.length > 0 ? (
          <div className="space-y-2">
            {costs.byModel.map((item: any, i: number) => {
              const maxCost = Math.max(...costs.byModel.map((m: any) => m.cost || 0));
              const pct = maxCost > 0 ? ((item.cost || 0) / maxCost) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-40 truncate">{item.model}</span>
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-foreground w-20 text-right">${(item.cost || 0).toFixed(4)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-6">Nenhum dado de custo disponível ainda.</p>
        )}
      </div>

      {/* Cost by Day */}
      {costs.byDay && costs.byDay.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Custo por Dia
          </h3>
          <div className="space-y-1.5">
            {costs.byDay.slice(-7).map((item: any, i: number) => {
              const maxCost = Math.max(...costs.byDay.map((d: any) => d.cost || 0));
              const pct = maxCost > 0 ? ((item.cost || 0) / maxCost) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-24">{item.date}</span>
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary/70 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-foreground w-20 text-right">${(item.cost || 0).toFixed(4)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Quick Test Tab ───────────────────────────────────────────────────────────

function QuickTestTab() {
  const [model, setModel] = useState("gpt-5.4-mini");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState<{ content: string; latency: number; tokens: number } | null>(null);
  const [open, setOpen] = useState(false);

  const testMutation = trpc.llm.quickTest.useMutation({
    onSuccess: (data) => setResult({
      content: data.response || data.error || "Sem resposta",
      latency: data.latencyMs,
      tokens: data.tokens || 0,
    }),
  });

  const MODELS = [
    // Premium (Março 2026)
    { id: "gpt-5.4", label: "GPT-5.4", provider: "OpenAI" },
    { id: "gpt-5.4-mini", label: "GPT-5.4 Mini", provider: "OpenAI" },
    { id: "gpt-5.4-nano", label: "GPT-5.4 Nano", provider: "OpenAI" },
    { id: "claude-opus-4-6-20260313", label: "Claude Opus 4.6", provider: "Anthropic" },
    { id: "claude-sonnet-4-6-20260313", label: "Claude Sonnet 4.6", provider: "Anthropic" },
    { id: "gemini-3.1-pro", label: "Gemini 3.1 Pro", provider: "Google" },
    // Utilitários
    { id: "deepseek-chat", label: "DeepSeek V3.2", provider: "DeepSeek" },
    { id: "deepseek-reasoner", label: "DeepSeek R1", provider: "DeepSeek" },
    // Mídia
    { id: "nano-banana-2", label: "Nano Banana 2", provider: "Nano Banana" },
    { id: "nano-banana-pro", label: "Nano Banana Pro", provider: "Nano Banana" },
  ];

  const selectedModel = MODELS.find((m) => m.id === model) || MODELS[0];

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <TestTube className="w-4 h-4 text-primary" /> Testar Modelo
        </h3>

        {/* Model Selector */}
        <div className="mb-4">
          <label className="text-xs text-muted-foreground mb-1.5 block">Modelo</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground hover:border-primary/40 transition-colors"
            >
              <span>{selectedModel.label} <span className="text-muted-foreground">({selectedModel.provider})</span></span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-50 py-1 max-h-60 overflow-y-auto">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setModel(m.id); setOpen(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-secondary/70 transition-colors ${m.id === model ? "bg-primary/5 text-primary" : "text-foreground"}`}
                  >
                    <span>{m.label}</span>
                    <span className="text-[10px] text-muted-foreground">{m.provider}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Prompt */}
        <div className="mb-4">
          <label className="text-xs text-muted-foreground mb-1.5 block">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Digite um prompt para testar..."
            rows={3}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60"
          />
        </div>

        <button
          onClick={() => testMutation.mutate({ modelId: model, prompt })}
          disabled={testMutation.isPending || !prompt.trim()}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all shadow-md shadow-primary/20"
        >
          {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Testar
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Resultado</h3>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{result.latency}ms</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{result.tokens} tokens</span>
            </div>
          </div>
          <div className="bg-background border border-input rounded-lg p-4 text-sm text-foreground whitespace-pre-wrap">
            {result.content}
          </div>
        </div>
      )}

      {testMutation.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Erro no teste</p>
            <p className="text-xs text-destructive/80 mt-1">{testMutation.error.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function LLMGateway() {
  const [activeTab, setActiveTab] = useState<GatewayTab>("models");

  const tabs: { id: GatewayTab; label: string; icon: typeof Cpu }[] = [
    { id: "models", label: "Modelos", icon: Cpu },
    { id: "costs", label: "Custos", icon: DollarSign },
    { id: "test", label: "Quick Test", icon: TestTube },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Cpu className="w-6 h-6 text-primary" />
          LLM Gateway
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie modelos, monitore custos e teste providers em tempo real
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-secondary/50 border border-border rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-primary" : ""}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "models" && <ModelsTab />}
      {activeTab === "costs" && <CostsTab />}
      {activeTab === "test" && <QuickTestTab />}
    </div>
  );
}
